import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';
import { ContentfulMeta } from '@/types';

interface ISEOProps extends ContentfulMeta {
  siteName?: string;
}

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultType: type
        siteName
        defaultTitle: title
        defaultDescription: description
        siteUrl: url
        defaultImage: image
      }
    }
    contentfulSettings {
      facebookApplication
    }
  }
`;

const SEO: React.FC<ISEOProps> = ({
  title,
  description,
  image,
  type,
  hidden,
}) => {
  const { pathname } = useLocation();
  const { site, contentfulSettings } = useStaticQuery(query);

  const {
    defaultTitle,
    defaultDescription,
    siteUrl,
    defaultImage,
    siteName,
    defaultType,
  } = site.siteMetadata;

  const seo = {
    title: title ?? defaultTitle,
    description: description ?? defaultDescription,
    image: image ? `https:${image.file.url}` : `${siteUrl}${defaultImage}`,
    url: `${siteUrl}${pathname}`,
    facebookApplication: contentfulSettings?.facebookApplication,
    siteName,
    type: type ?? defaultType,
    hidden,
  };

  useEffect(() => {
    window.document.getElementsByTagName(`html`)[0].lang = `en`;
  }, []);

  return (
    <Helmet title={seo.title} titleTemplate={seo.title}>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta
        name="facebook-domain-verification"
        content="fwgqzi0itengnje8c48xc1ukbwmu7s"
      />
      {seo.url && <meta property="og:url" content={seo.url} />}
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.title && <meta property="og:image:alt" content={seo.title} />}
      {seo.type && <meta property="og:type" content={seo.type} />}
      {seo.facebookApplication && (
        <meta property="fb:app_id" content={seo.facebookApplication} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      {seo.title && <meta name="twitter:title" content={seo.title} />}
      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}
      {seo.image && <meta name="twitter:image" content={seo.image} />}
      {!!seo.hidden && <meta name="robots" content="noindex,nofollow" />}
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/assets/images/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/assets/images/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/assets/images/favicon-16x16.png"
      />
    </Helmet>
  );
};

export default SEO;
